import type { JSONContent } from "@tiptap/react";
import { parseTemplate } from "@/parser/templateParser";
import { serializeTemplate } from "@/parser/templateSerializer";
import type { TemplateMeta } from "@/parser/templateSerializer";
import { contentToNodes } from "@/components/unified/templateToDocument";
import { documentToContentNodes } from "@/components/unified/documentToContentNodes";
import { initSubTemplateData } from "@/utils/templateResolution";
import { useTemplateStore } from "@/store/useTemplateStore";
import type { SubTemplateData } from "@/types/evaluation";

export interface EditorInitData {
  meta: TemplateMeta;
  initialDoc: JSONContent | null;
  initialSource: string;
  name: string;
  variables: string[];
  subTemplateValues?: Record<string, SubTemplateData>;
}

function computeBuildInit(template: { id: string; source: string } | undefined): EditorInitData {
  const parsed = parseTemplate(template?.source ?? "---\nname: Untitled\n---\n");
  const meta: TemplateMeta = {
    category: parsed.content.category,
    id: template?.id,
    isSubTemplate: parsed.content.isSubTemplate,
    name: parsed.content.name,
    variables: parsed.content.variables,
  };
  const initialDoc: JSONContent = { content: contentToNodes(parsed.content.content), type: "doc" };
  const getParsedContent = useTemplateStore.getState().getParsedContent;
  const initialSubTemplateValues = initSubTemplateData(parsed.content.content, getParsedContent);
  return {
    initialDoc,
    initialSource: serializeTemplate(parsed.content.content, meta),
    meta,
    name: parsed.content.name,
    subTemplateValues: initialSubTemplateValues,
    variables: parsed.content.variables,
  };
}

function computeFillInit(
  evaluation:
    | {
        templateName: string;
        templateId: string;
        name: string;
        variables: string[];
        unifiedDocument?: Record<string, unknown>;
      }
    | undefined,
): EditorInitData {
  const evalMeta = {
    id: evaluation?.templateId,
    name: evaluation?.templateName ?? "",
  } as TemplateMeta;
  const initialDoc = (evaluation?.unifiedDocument as JSONContent | null) ?? null;
  return {
    initialDoc,
    initialSource: initialDoc
      ? serializeTemplate(documentToContentNodes(initialDoc), evalMeta)
      : "",
    meta: evalMeta,
    name: evaluation?.name ?? "",
    variables: evaluation?.variables ?? [],
  };
}

export function computeEditorInit(
  mode: "build" | "fill",
  template: { id: string; source: string } | undefined,
  evaluation:
    | {
        templateName: string;
        templateId: string;
        name: string;
        variables: string[];
        unifiedDocument?: Record<string, unknown>;
      }
    | undefined,
): EditorInitData {
  return mode === "build" ? computeBuildInit(template) : computeFillInit(evaluation);
}
